import Swiper from 'swiper';
import { A11y, Keyboard, Navigation } from 'swiper/modules';
import { SwiperOptions } from 'swiper/types';

type JumpLinks = {
  element: HTMLElement | null;
  appHeaderHeight: number;
  jumpLinks: NodeList | null;
  swiper: any | null;
  headerScrollOffset: number;
  anchorIntersectionObserver: IntersectionObserver | null;
  // Parallel to `jumpLinks`: anchors[i] is the in-page anchor element targeted
  // by jumpLinks[i]'s href, or null if that href doesn't resolve. Targets are
  // standalone anchors (`<a id="…">`) rather than the section elements
  // themselves. Keeping the arrays index-aligned means an anchors index can
  // always be passed straight into setActiveJumpLink / swiper.slideTo, which
  // both operate on jumpLinks.
  anchors: (HTMLElement | null)[];
  suppressActiveUpdates: boolean;
  init: () => void;
  initSwiper: (swiperContainer: HTMLElement) => void;
  setActiveJumpLink: (index: number) => void;
};

const JumpLinks: JumpLinks = {
  element: document.querySelector('[data-hook=jumpLinks]'),
  appHeaderHeight: 0,
  jumpLinks: null,
  swiper: null,
  headerScrollOffset: 0,
  anchorIntersectionObserver: null,
  anchors: [],
  suppressActiveUpdates: false,

  init() {
    if (!this.element) return;

    // Gather jump links
    this.jumpLinks = this.element.querySelectorAll('nav.jump-links .swiper-slide a') as NodeList;
    if (!this.jumpLinks) return;

    // Setup Swiper
    const swiperContainer: HTMLElement | null = this.element.querySelector('.swiper');
    if (!swiperContainer) return;
    this.initSwiper(swiperContainer);

    // Discover the anchor targets by mapping each rendered jump link
    // (`href="#id"`) to the element carrying that id. Those targets are
    // standalone in-page anchors, not the section elements. The array stays
    // parallel to `this.jumpLinks` (same length, null for any link whose href
    // doesn't resolve) so an anchors index is always equivalent to a jumpLinks
    // index.
    this.anchors = Array.from(this.jumpLinks).map((link) => {
      if (!(link instanceof HTMLAnchorElement)) return null;
      const id = link.getAttribute('href')?.slice(1) ?? '';
      return id ? document.getElementById(id) : null;
    });

    // Configure appHeaderResizeObserver
    // Used to watch appHeader height and adjust JumpLink sticky position
    const appHeader = document.querySelector('[data-hook=appHeader]') as HTMLElement;

    // Recompute which anchor is active from the current scroll positions and
    // reflect it in the nav + URL. Extracted so it can run both from the
    // IntersectionObserver (live, during scroll) and as a one-shot when
    // scrolling settles / updates are re-enabled — see the callers below for
    // why the observer alone isn't enough.
    const refreshActiveAnchor = () => {
      // Pick the anchor currently closest to the fold line from above (the
      // largest top value still at-or-above foldLine). Walking the full array
      // (rather than breaking on first non-match) means we tolerate null
      // entries and don't assume `anchors` is in document order. Falls back to
      // index 0 when the user is above all anchors.
      const foldLine = this.headerScrollOffset;

      // An anchor near the end of the page can never bring its top all the way
      // up to the fold line — the page runs out of scroll first. Once we're at
      // max scroll, relax the rule so those trailing anchors still qualify;
      // otherwise the last jump link could never activate (a bookmark/deep-link
      // to it would highlight the anchor just before it instead). Guarded on
      // actual scrollability so a page that fits in the viewport isn't mistaken
      // for being "at the bottom".
      const doc = document.documentElement;
      const atBottom =
        doc.scrollHeight > window.innerHeight && window.scrollY + window.innerHeight >= doc.scrollHeight - 2;

      let activeIndex = 0;
      let activeTop = -Infinity;
      for (let i = 0; i < this.anchors.length; i++) {
        const anchor = this.anchors[i];
        if (!anchor) continue;
        const top = anchor.getBoundingClientRect().top;
        if ((top - foldLine <= 1 || atBottom) && top > activeTop) {
          activeIndex = i;
          activeTop = top;
        }
      }

      this.setActiveJumpLink(activeIndex);
      this.swiper?.slideTo(activeIndex, 500);

      // Reflect the active anchor in the URL only once one has actually reached
      // the sticky nav. `activeTop` stays -Infinity until an anchor crosses the
      // fold line, so at the top of the page (above all anchors) we leave the
      // URL alone instead of eagerly writing the first anchor's id, and we clear
      // a stale hash if the user scrolls back up above all anchors. Uses
      // replaceState so we never trigger navigation or pollute history.
      const anchorId = activeTop > -Infinity ? this.anchors[activeIndex]?.id : null;
      if (anchorId) {
        if (window.location.hash !== `#${anchorId}`) {
          history.replaceState(null, '', `#${anchorId}`);
        }
      } else if (window.location.hash) {
        // Only clear a hash we own — one matching one of this component's anchor
        // ids. A hash set by another component (or a deep-link to an unrelated
        // fragment) is left untouched so we don't wipe its state.
        const currentId = window.location.hash.slice(1);
        if (this.anchors.some((anchor) => anchor?.id === currentId)) {
          history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      }
    };

    // Builds (or rebuilds) the anchor IntersectionObserver. Called from
    // `updateStickyOffset` so the rootMargin always tracks the live sticky
    // stack — the appHeader height changes when the logo image loads, when
    // the viewport is resized, etc.
    const buildAnchorObserver = () => {
      this.anchorIntersectionObserver?.disconnect();

      this.anchorIntersectionObserver = new IntersectionObserver(
        () => {
          if (this.suppressActiveUpdates) return;
          refreshActiveAnchor();
        },
        {
          // Active zone: from the top of the viewport down to the fold line
          // (= the bottom of the sticky stack). An anchor's IntersectionObserver
          // "enter" event therefore fires at the exact moment its top edge
          // crosses the fold line — the same position the browser lands on
          // when the corresponding jump link is clicked. Because the zone is
          // anchored to the top of the viewport, every reasonably-tall section
          // also occupies the zone for the full distance it scrolls upward,
          // so a discrete Windows mouse-wheel notch cannot skip it between
          // two animation frames.
          rootMargin: `0px 0px -${Math.max(0, window.innerHeight - this.headerScrollOffset)}px 0px`,
          threshold: 0,
        },
      );

      this.anchors.forEach((anchor) => {
        if (anchor) this.anchorIntersectionObserver?.observe(anchor);
      });
    };

    const updateStickyOffset = (appHeaderHeight: number) => {
      if (!this.element) return;
      this.headerScrollOffset = appHeader.offsetHeight + this.element.offsetHeight;
      this.appHeaderHeight = appHeaderHeight;
      this.element.style.top = `${appHeaderHeight}px`;
      // Publish offset to CSS so .jump-link-anchor scroll-margin-top tracks the live sticky stack
      document.documentElement.style.setProperty('--jump-links-scroll-offset', `${this.headerScrollOffset}px`);
      // Rebuild the anchor observer so its rootMargin tracks the live offset.
      if (this.anchors.length) {
        buildAnchorObserver();
      }
    };
    updateStickyOffset(appHeader.offsetHeight);
    const appHeaderResizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        updateStickyOffset(entry.contentRect.height);
      }
    });
    appHeaderResizeObserver.observe(appHeader);

    // Rebuild the anchor observer on window resize so its rootMargin (which
    // depends on window.innerHeight) tracks the live viewport. This handles
    // viewport changes that don't also resize the appHeader (e.g. a desktop
    // user dragging the bottom edge of the browser window).
    let resizeRafScheduled = false;
    window.addEventListener(
      'resize',
      () => {
        if (resizeRafScheduled) return;
        resizeRafScheduled = true;
        requestAnimationFrame(() => {
          resizeRafScheduled = false;
          if (this.anchors.length) buildAnchorObserver();
        });
      },
      { passive: true },
    );

    // Recompute once scrolling settles. The IntersectionObserver only reacts to
    // zone crossings, but an anchor can ease to rest exactly on the fold line
    // without ever entering the zone — the empty anchor is only 1px tall, and
    // the appHeader shrinks as you scroll, moving the fold onto the resting
    // anchor. In that case the observer never re-fires at the final position
    // and the previously-active link would stick, so we refresh on scroll-idle.
    let scrollIdleTimer: number | undefined;
    window.addEventListener(
      'scroll',
      () => {
        if (scrollIdleTimer !== undefined) window.clearTimeout(scrollIdleTimer);
        scrollIdleTimer = window.setTimeout(() => {
          if (!this.suppressActiveUpdates && this.anchors.length) refreshActiveAnchor();
        }, 100);
      },
      { passive: true },
    );

    // Configure jumpLinkIntersectionObserver
    // Used to determine when to activate the is-pinned class on jumpLink nav
    const jumpLinkIntersectionObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.element?.classList.add('is-pinned');
          } else {
            this.element?.classList.remove('is-pinned');
          }
        });
      },
      {
        rootMargin: `-${appHeader.offsetHeight - 40}px 0px -${window.innerHeight - this.appHeaderHeight}px 0px`,
        threshold: 0,
      },
    );
    jumpLinkIntersectionObserver?.observe(this.element);

    // Hash-on-load: set active state explicitly and suppress observer-driven
    // updates while we settle the target anchor under the sticky nav, so the
    // active link doesn't briefly flash other sections.
    if (window.location.hash) {
      const targetId = window.location.hash.slice(1);
      const targetIndex = this.anchors.findIndex((a) => a?.id === targetId);
      const targetAnchor = targetIndex >= 0 ? this.anchors[targetIndex] : null;
      if (targetAnchor) {
        this.suppressActiveUpdates = true;
        this.swiper?.slideTo(targetIndex, 0);
        this.setActiveJumpLink(targetIndex);

        // The browser performs its scroll-to-fragment at DOMContentLoaded,
        // before images/fonts below the sticky nav finish loading. As those
        // load they push the target anchor down, so the initial scroll lands
        // short — the anchor never reaches the nav and a neighbouring link
        // activates instead (worst on the last one). Re-pin the anchor under
        // the nav each frame until its position holds steady (or a short
        // deadline passes), absorbing those late layout shifts, then re-enable
        // observer-driven updates. scrollIntoView honours the target's
        // scroll-margin-top (= --jump-links-scroll-offset), landing it right
        // below the sticky stack.
        const startTime = performance.now();
        let lastTop = Number.NaN;
        let stableFrames = 0;
        const pinUntilSettled = () => {
          targetAnchor.scrollIntoView();
          const top = Math.round(targetAnchor.getBoundingClientRect().top);
          stableFrames = top === lastTop ? stableFrames + 1 : 0;
          lastTop = top;
          if (stableFrames < 3 && performance.now() - startTime < 2000) {
            requestAnimationFrame(pinUntilSettled);
          } else {
            this.suppressActiveUpdates = false;
            // The anchor has come to rest under the nav; recompute the active
            // link now rather than waiting for an observer crossing that never
            // comes when it settles exactly on the fold line.
            refreshActiveAnchor();
          }
        };
        requestAnimationFrame(pinUntilSettled);
      }
    }

    // Suppress observer-driven active updates for a beat after a click/focus
    // activation, so the browser's ensuing scroll doesn't let the observer
    // override the link we just activated. A single debounced timer (rather
    // than one per event) means rapid activations — e.g. tabbing quickly
    // through a section — keep pushing the re-enable out instead of an earlier
    // timer firing mid-navigation and flashing the wrong link.
    let suppressTimer: number | undefined;
    const suspendActiveUpdates = () => {
      this.suppressActiveUpdates = true;
      if (suppressTimer !== undefined) window.clearTimeout(suppressTimer);
      suppressTimer = window.setTimeout(() => {
        this.suppressActiveUpdates = false;
        // Settle the active link explicitly: if the scroll ended while we were
        // suppressed, no crossing/scroll event will re-run the observer.
        refreshActiveAnchor();
      }, 500);
    };

    // Click handler: activate the clicked link and hold off the observer while
    // the browser performs the smooth scroll to the target so intermediate
    // sections don't briefly flash as active.
    this.jumpLinks.forEach((linkItem) => {
      linkItem.addEventListener('click', () => {
        if (!(linkItem instanceof HTMLAnchorElement)) return;

        const targetIndex = Array.prototype.indexOf.call(this.jumpLinks, linkItem) as number;

        suspendActiveUpdates();
        this.swiper?.slideTo(targetIndex, 500);
        this.setActiveJumpLink(targetIndex);
      });
    });

    // `:focus-visible` isn't universally supported; `matches(':focus-visible')`
    // throws a SyntaxError in browsers that don't recognise the selector.
    // Guard it so a throw can't break focus handling, and treat unsupported
    // browsers as "not focus-visible" (i.e. this enhancement is skipped there).
    const isFocusVisible = (el: HTMLElement): boolean => {
      try {
        return el.matches(':focus-visible');
      } catch {
        return false;
      }
    };

    // Keyboard focus tracking: when a focus-visible element inside a jump-link
    // section receives focus (e.g. tabbing through the page), activate that
    // section and make sure the focused element isn't left hidden behind the
    // sticky nav. Gated on :focus-visible so pointer clicks don't move the
    // scroll position; `focusin` (not `focus`) so the event bubbles up to here.
    document.addEventListener('focusin', (event) => {
      const focused = event.target;
      if (!(focused instanceof HTMLElement) || !isFocusVisible(focused)) return;

      // Each anchor is an empty marker rendered immediately before its section,
      // so a focused element lives in the anchor's next element sibling (the
      // section), not the anchor itself. `anchors` is index-parallel to
      // `jumpLinks`, so the matching anchor's index is also the active
      // jump-link index.
      const index = this.anchors.findIndex((anchor) => anchor?.nextElementSibling?.contains(focused));
      if (index < 0) return;

      // Mirror the click behaviour: mark the section active and hold off the
      // observer briefly so other sections don't flash during the scroll
      // correction below.
      suspendActiveUpdates();
      this.swiper?.slideTo(index, 500);
      this.setActiveJumpLink(index);
      const anchorId = this.anchors[index]?.id;
      if (anchorId && window.location.hash !== `#${anchorId}`) {
        history.replaceState(null, '', `#${anchorId}`);
      }

      // Reveal the focused element only when the sticky stack is actually
      // covering it (its top sits above the fold line). Measured next frame so
      // any native focus scrolling has already settled, and nudged instantly so
      // the element isn't left obscured mid-animation. Nudging only in the
      // covered case means focus deeper inside a tall section isn't yanked back
      // up to the section top.
      requestAnimationFrame(() => {
        const top = focused.getBoundingClientRect().top;
        if (top < this.headerScrollOffset) {
          window.scrollBy({ top: top - this.headerScrollOffset });
        }
      });
    });
  },

  initSwiper(swiperContainer) {
    if (swiperContainer) {
      const nextButton = this.element?.querySelector('.swiper-button-next') as HTMLElement;
      const prevButton = this.element?.querySelector('.swiper-button-prev') as HTMLElement;
      const swiperOptions: SwiperOptions = {
        modules: [A11y, Keyboard, Navigation],
        slidesPerView: 'auto',
        slidesPerGroup: 1,
        spaceBetween: 20,
        loop: false,
        slideToClickedSlide: true,
        freeMode: true,
        navigation: {
          nextEl: nextButton, // '.swiper-button-next',
          prevEl: prevButton, // '.swiper-button-prev',
        },
        a11y: {
          enabled: true,
          slideRole: 'listitem',
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
      };
      // skipcq: JS-R1002
      this.swiper = new Swiper(swiperContainer, swiperOptions);
    }
  },

  setActiveJumpLink(targetIndex: number) {
    this.jumpLinks?.forEach((linkItem, linkItemIndex) => {
      if (linkItem instanceof HTMLAnchorElement) {
        if (linkItemIndex === targetIndex) {
          linkItem.classList.add('is-active');
        } else {
          linkItem.classList.remove('is-active');
        }
      }
    });
  },
};

export default JumpLinks;
